import React, { useState, useEffect } from 'react';
import { Card, Space, Spin, message, DatePicker, Select, Table, Button } from 'antd';
import moment from 'moment';
import {
  getCircuitosByDay,
  getCircuitosByVendedor,
  getEncuestas,
  getRespuestasEncuesta,
  verificarRespuesta,
  verificarRespuestasMasivo,
  getConteoRespuestasNoVerificadas
} from '../../services/verificarEncuestas';
import Layouts from '../../components/Layouts';
import Filtros from './components/Filtros';
import TablaRespuestas from './components/TablaRespuestas';
import { useSelector } from 'react-redux';

const VerificarEncuestas = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [circuitos, setCircuitos] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [encuestas, setEncuestas] = useState([]);
  const [selectedEncuesta, setSelectedEncuesta] = useState(null);
  const [respuestas, setRespuestas] = useState([]);
  const [conteoRespuestas, setConteoRespuestas] = useState({});
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const userRoleID = useSelector((state) => state.userdata.role?.name);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  
  useEffect(() => {
    loadEncuestas();
  }, []);

  useEffect(() => {
    if (selectedEncuesta && selectedDate) {
      const dayNumber = moment(selectedDate).isoWeekday();
      setSelectedDay(dayNumber);
      loadCircuitosByDay(dayNumber);
    }
  }, [selectedEncuesta, selectedDate]);

  useEffect(() => {
    if (circuitos.length > 0 && selectedEncuesta) {
      loadVendedoresByDay();
    }
  }, [circuitos, selectedEncuesta]);

  useEffect(() => {
    if (selectedVendedor && selectedEncuesta) {
      loadClientesByVendedor();
    }
  }, [selectedVendedor, selectedEncuesta]);

  useEffect(() => {
    if (selectedDate) {
      fetchRespuestas();
    }
  }, [selectedDate, selectedCliente, selectedEncuesta]);

  const loadEncuestas = async () => {
    try {
      const data = await getEncuestas();
      setEncuestas(data);
      
      const conteos = {};
      for (const encuesta of data) {
        const count = await getConteoRespuestasNoVerificadas(encuesta.id);
        conteos[encuesta.id] = count;
      }
      setConteoRespuestas(conteos);
    } catch (error) {
      console.error('Error al cargar encuestas:', error);
      message.error('Error al cargar encuestas');
    }
  };

  const loadCircuitosByDay = async (day) => {
    try {
      const data = await getCircuitosByDay(day, userRoleID, userBodega);
      setCircuitos(data);
    } catch (error) {
      console.error('Error al cargar circuitos:', error);
      message.error('Error al cargar circuitos');
    }
  };

  const loadVendedoresByDay = () => {
    const vendedoresUnicos = circuitos.reduce((acc, circuito) => {
      if (circuito.vendedor && !acc.find(v => v.id === circuito.vendedor.id)) {
        acc.push({
          id: circuito.vendedor.id,
          nombre: `${circuito.vendedor.first_name} ${circuito.vendedor.last_name}`
        });
      }
      return acc;
    }, []);
    setVendedores(vendedoresUnicos);
  };

  const loadClientesByVendedor = async () => {
    try {
      const data = await getCircuitosByVendedor(selectedVendedor, userRoleID, userBodega);
      
      const clientesUnicos = data.reduce((acc, circuito) => {
        if (circuito.clientes) {
          circuito.clientes.forEach(cliente => {
            if (cliente.clientes_id && !acc.find(c => c.id === cliente.clientes_id.id)) {
              acc.push({
                id: cliente.clientes_id.id,
                nombre: `${cliente.clientes_id.first_name} ${cliente.clientes_id.last_name}`
              });
            }
          });
        }
        return acc;
      }, []);
      
      console.log('Clientes procesados:', clientesUnicos); // Para debug
      setClientes(clientesUnicos);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      message.error('Error al cargar clientes');
    }
  };

  const fetchRespuestas = async (page = 1) => {
    setLoading(true);
    try {
      const { data, total } = await getRespuestasEncuesta({
        fecha: selectedDate ? selectedDate.toISOString() : null,
        vendedorId: selectedVendedor,
        clienteId: selectedCliente,
        encuestaId: selectedEncuesta,
        page,
        limit: pagination.pageSize
      });

      setRespuestas(data);
      setTotal(total);
      setPagination(prev => ({ ...prev, total }));
    } catch (error) {
      console.error('Error fetching respuestas:', error);
      message.error('Error al cargar las respuestas');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificarRespuesta = async (respuestaId) => {
    try {
      await verificarRespuesta(respuestaId);
      fetchRespuestas();
      message.success('Respuesta verificada correctamente');
    } catch (error) {
      console.error('Error verificando respuesta:', error);
      message.error('Error al verificar la respuesta');
    }
  };

  const handleVerificarTodas = async () => {
    try {
      const respuestasNoVerificadas = respuestas
        .filter(resp => !resp.verificada)
        .map(resp => resp.id);
      
      if (respuestasNoVerificadas.length === 0) {
        message.info('No hay respuestas para verificar');
        return;
      }

      await verificarRespuestasMasivo(respuestasNoVerificadas);
      fetchRespuestas();
      message.success('Todas las respuestas verificadas correctamente');
    } catch (error) {
      console.error('Error verificando respuestas:', error);
      message.error('Error al verificar las respuestas');
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date && date.isValid() ? date : null);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleVendedorChange = (value) => {
    setSelectedVendedor(value);
    setSelectedCliente(null);
    setClientes([]);
    if (value) {
      loadClientesByVendedor();
    }
  };
  
  const handleEncuestaChange = (value) => {
    setSelectedEncuesta(value);
    setSelectedDate(moment());
    setSelectedDay(null);
    setSelectedVendedor(null);
    setSelectedCliente(null);
    setCircuitos([]);
    setVendedores([]);
    setClientes([]);
    setRespuestas([]);
  };

  const handleResetFiltros = () => {
    setSelectedDate(null);
    setSelectedVendedor(null);
    setSelectedCliente(null);
    setSelectedEncuesta(null);
    setCircuitos([]);
    setVendedores([]);
    setClientes([]);
    setRespuestas([]);
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    fetchRespuestas(1);
  };

  return (
    <Layouts>
      <Card title="Verificar Encuestas">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Filtros
            selectedDate={selectedDate}
            selectedVendedor={selectedVendedor}
            selectedCliente={selectedCliente}
            selectedEncuesta={selectedEncuesta}
            vendedores={vendedores}
            clientes={clientes}
            encuestas={encuestas}
            conteoRespuestas={conteoRespuestas}
            respuestas={respuestas}
            onDateChange={handleDateChange}
            onVendedorChange={handleVendedorChange}
            onClienteChange={setSelectedCliente}
            onEncuestaChange={handleEncuestaChange}
            onVerificarTodas={handleVerificarTodas}
          />
          <Button onClick={handleResetFiltros} type="default">
            Resetear filtros
          </Button>
          <Spin spinning={loading}>
            <TablaRespuestas
              loading={loading}
              respuestas={respuestas}
              onVerificarRespuesta={handleVerificarRespuesta}
              pagination={{
                ...pagination,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchRespuestas(page);
                }
              }}
            />
          </Spin>
        </Space>
      </Card>
    </Layouts>
  );
};

export default VerificarEncuestas;